/**
 * ⌘K palette (OutlierOS replica: creators + pages with descriptions and a category chip), upgraded with
 * arrow keys and Enter (OutlierOS showed "Enter to jump" but ignored it).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Search, X } from 'lucide-react';
import { cn } from '../cn';

export interface PaletteItem { id: string; label: string; description: string; category: string; href: string }

export function CommandPalette({ open, onClose, items, onNavigate }: { open: boolean; onClose: () => void; items: PaletteItem[]; onNavigate: (href: string) => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      setQ('');
      setSel(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^@/, '');
    return s ? items.filter((i) => `${i.label} ${i.description}`.toLowerCase().replace(/@/g, '').includes(s)) : items;
  }, [q, items]);
  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    list.current?.querySelector(`[data-idx="${sel}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);
  if (!open) return null;
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(results.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter' && results[sel]) { e.preventDefault(); onNavigate(results[sel]!.href); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 px-4 pt-[12vh] backdrop-blur-md animate-fade-in" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label="Search" className="w-full max-w-xl animate-zoom-in overflow-hidden rounded-2xl border border-border bg-card shadow-2xl" onKeyDown={onKey}>
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 text-primary" />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators, collections or pages…"
            role="combobox" aria-expanded="true" aria-controls="palette-results" aria-activedescendant={results[sel] ? `pal-${results[sel]!.id}` : undefined}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
          <button onClick={onClose} className="rounded-md border border-border bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">ESC</button>
          <button aria-label="Close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div ref={list} id="palette-results" role="listbox" className="max-h-[360px] overflow-y-auto p-2">
          {results.map((r, i) => (
            <button key={r.id} id={`pal-${r.id}`} data-idx={i} role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onClick={() => onNavigate(r.href)}
              className={cn('flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left', i === sel ? 'bg-primary/10' : 'hover:bg-secondary')}>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2"><span className={cn('truncate text-sm font-bold', i === sel && 'text-primary')}>{r.label}</span>
                  <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{r.category}</span></span>
                <span className="block truncate text-xs text-muted-foreground">{r.description}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
          {!results.length && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching creators or pages found.</p>}
        </div>
        <div className="flex justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>Press <b>ESC</b> to close</span><span><b>↑↓</b> to move · <b>Enter</b> to jump</span>
        </div>
      </div>
    </div>
  );
}
